import { ForeColorType, HeaderPositionType, PaletteColorType, PositionType, TextStyleType } from "../constants";
import { BaseCategory } from "./BaseCategory";
import { SlideCategories } from "./CategoryController";
import { SlideModelBuilder } from "./SlideModelBuilder";

export class PillarCategory extends BaseCategory {
    get name() {
        return "Pillars";
    }

    get type() {
        return SlideCategories.PILLAR;
    }

    get icon() {
        return null;
    }

    get description() {
        return "The content presents pillars, principles, or foundations arranged horizontally like columns supporting a structure. Use this for frameworks, core values, strategic pillars, or key supporting concepts that stand side-by-side.";
    }

    get slideTypes() {
        return [];
    }

    getPrompt() {
        return `Create a set of pillars, principles, or foundational concepts arranged horizontally like columns.
        Include a header cell at the top (like a roof) with an overarching title or theme.
        Each pillar should have a title and description and optionally an icon. All pillars will have equal width and height.
        Optionally include a footer cell at the bottom (like a floor/foundation) with a summary or conclusion.
        Typically 3-5 pillar items work best for this layout.`;
    }

    getPromptSchema() {
        return {
            header: "text content for a header cell at the top of the stack",
            items: this.getItemListSchema(),
            footer: "text content for a footer cell at the bottom of the stack",
            conclusion: "an optional short conclusion or takeaway for the slide (displayed below the stack)",
        };
    }

    validateGeneratedModel(model) {
        if (!this.validateArray(model.items)) {
            return false;
        }
        return true;
    }

    getSlideModels(model, options = {}) {
        let models = [
            this.buildPillarSlide(model, options),
        ];

        return models;
    }

    buildPillarSlide(model, options = {}) {
        let builder = new SlideModelBuilder("stackDiagram", model);
        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.TOP });

        // Build items array with optional header, pillars, and optional footer
        const items = [];
        const hasHeader = model.header && model.header.length > 0;
        const hasFooter = model.footer && model.footer.length > 0;

        // Add header cell if present
        if (hasHeader) {
            items.push({
                componentType: "TextAndImage",
                cellColor: PaletteColorType.THEME,
                childElement: {
                    backdropPadding: 10,
                    text: builder.createSingleBlockText(model.header, TextStyleType.TITLE)
                }
            });
        }

        // Add pillar items
        model.items.filter(o => o).forEach(item => {
            items.push({
                componentType: "TextAndImage",
                cellColor: ForeColorType.COLORFUL,
                childElement: {
                    backdropPadding: 10,
                    textPosition: PositionType.TOP,
                    text: {
                        blocks: [
                            builder.createMediaBlock(item.icon, 50, {
                                iconScale: 1.5,
                                frameType: "none"
                            }),
                            builder.createBlock(item.title, TextStyleType.TITLE),
                            builder.createBlock(item.description, TextStyleType.BODY)
                        ].filter(b => b)
                    }
                }
            });
        });

        // Add footer cell if present
        if (hasFooter) {
            items.push({
                componentType: "TextAndImage",
                cellColor: PaletteColorType.THEME,
                childElement: {
                    backdropPadding: 10,
                    text: builder.createSingleBlockText(model.footer, TextStyleType.CAPTION)
                }
            });
        }

        // Get appropriate layout based on pillar count and header/footer presence
        const pillarCount = model.items?.length || 3;
        const layout = this.getStackLayout(pillarCount, hasHeader, hasFooter);

        builder.createPrimary({
            fullBleed: false,
            showGutter: true,
            cellColor: ForeColorType.COLORFUL,
            gridLayout: layout,
            items: items
        });

        if (options.citeSources) {
            builder.createSourcesAttribution(model.sources);
        }

        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }

        return builder.model;
    }

    getStackLayout(pillarCount, hasHeader, hasFooter) {
        // Choose column count that's divisible by pillar count for equal widths
        const cols = this.getColumnCount(pillarCount);
        const rows = 12;
        const headerHeight = 2;
        const footerHeight = 2;

        const cells = [];
        let currentY = 0;

        // Add header cell if present
        if (hasHeader) {
            cells.push({
                x: 0,
                y: currentY,
                width: cols,
                height: headerHeight
            });
            currentY += headerHeight;
        }

        // Calculate available space for pillars
        const availableRows = rows - currentY - (hasFooter ? footerHeight : 0);

        // Generate pillar cells based on count
        const pillarCells = this.generatePillarCells(pillarCount, cols, currentY, availableRows);
        cells.push(...pillarCells);

        // Add footer cell if present
        if (hasFooter) {
            cells.push({
                x: 0,
                y: rows - footerHeight,
                width: cols,
                height: footerHeight
            });
        }

        return {
            cols,
            rows,
            cells
        };
    }

    getColumnCount(pillarCount) {
        // Return column count that's divisible by pillar count
        // Ensures each pillar has exactly the same width
        const colCounts = {
            1: 12,
            2: 12,  // 6 each
            3: 12,  // 4 each
            4: 12,  // 3 each
            5: 15,  // 3 each
            6: 12,  // 2 each
        };

        return colCounts[pillarCount] || 12;
    }

    generatePillarCells(pillarCount, cols, startY, availableHeight) {
        const cells = [];

        // All pillars have equal width and height, arranged horizontally
        const pillarWidth = cols / pillarCount;

        for (let i = 0; i < pillarCount; i++) {
            cells.push({
                x: i * pillarWidth,
                y: startY,
                width: pillarWidth,
                height: availableHeight
            });
        }

        return cells;
    }
}
